import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { ValueAccessorBase } from '~/component/value-accessor.base';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';

const NUMBER_OF_TIMESLOT_TO_DISPLAY = 25;

@Component({
  selector: 'app-organizer-timeslot-edition-shared-select-timeslots',
  templateUrl: './select-timeslots.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: OrganizerTimeslotEditionSharedSelectTimeslotsComponent,
      multi: true,
    },
  ],
})
export class OrganizerTimeslotEditionSharedSelectTimeslotsComponent
  extends ValueAccessorBase<unknown>
  implements OnInit, OnDestroy
{
  @Input() public activity: Activity;
  @Input() public previousUrl: string;
  @Input() public timeslots: Timeslot[] = [];

  @Output() public updateTimeslots = new EventEmitter<void>();

  private readonly subscriptions = new Subscription();

  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  private isSubmitButtonDisabled = false;

  public displayedTimeslots: Timeslot[] = [];
  public formGroup;
  public isOverlayDisplayed = false;
  public numberOfTimeslotToDisplay = NUMBER_OF_TIMESLOT_TO_DISPLAY;
  public overlayStatus = SuccessStatusEnum.LOADING;

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.displayedTimeslots = this.timeslots.slice(
      0,
      this.numberOfTimeslotToDisplay
    );

    this.formGroup = new FormGroup(
      {
        'select-all': new FormControl(false),
        timeslots: new FormGroup({}),
      },
      [this.hasSelectedTimeslotValidator()]
    );

    this.addFormControls();
  }

  public get isFormValid(): boolean {
    return this.formGroup.valid && !this.isSubmitButtonDisabled;
  }

  private addFormControls(): void {
    const isSelected = this.formGroup.get('select-all')?.value;

    this.displayedTimeslots.forEach((timeslot, index) => {
      this.formGroup
        .get('timeslots')
        // Set the checkbox value at true, if "select-all" is checked or if it's the first checkbox //
        .addControl(timeslot.identifier, new FormControl(!index || isSelected));
    });
  }

  private hasSelectedTimeslotValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return Object.values(control.value.timeslots).some(
        (value) => value === true
      )
        ? null
        : { missingSelection: true };
    };
  }

  public loadMoreTimeslots(): void {
    const previousNumberOftimeslotToDisplay = this.numberOfTimeslotToDisplay;

    this.numberOfTimeslotToDisplay += NUMBER_OF_TIMESLOT_TO_DISPLAY;
    this.displayedTimeslots = this.displayedTimeslots?.concat(
      this.timeslots?.slice(
        previousNumberOftimeslotToDisplay,
        this.numberOfTimeslotToDisplay
      )
    );

    this.addFormControls();
  }

  public submitted(): void {
    this.isSubmitButtonDisabled = true;
    this.updateTimeslots.emit();
  }

  public toggleTimeslotSelection(): void {
    this.formGroup.get('select-all').setValue(false);
    this.value = this.formGroup.value;
  }

  public toggleTimeslotsSelection(): void {
    this.displayedTimeslots.forEach((timeslot) => {
      this.formGroup
        .get('timeslots')
        .get(timeslot.identifier)
        .setValue(this.formGroup.get('select-all').value);
    });
    this.value = this.formGroup.value;
  }
}
